import React, { useEffect, useState, useCallback } from "react";
import {
  Container,
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Grid,
  CircularProgress,
  Alert,
  Snackbar,
  Tabs,
  Tab,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  FormHelperText,
  FormControlLabel,
  Card,
  CardContent,
  Divider,
  InputAdornment,
  Checkbox,
} from "@mui/material";
import {
  Upload,
  Add,
  Category as CategoryIcon,
  Link as LinkIcon,
  Description,
  Source as SourceIcon,
  Comment,
  SubdirectoryArrowRight,
} from "@mui/icons-material";
import { useFormik } from "formik";
import * as Yup from "yup";
import Header from "../components/Header";
import ExcelUploader from "../components/ExcelUploader";
import { categoryApi, researchApi } from "../api";
import uploadApi from "../api/uploadApi";
import { useNavigate, useParams } from "react-router-dom";
import { CalendarMonth } from "@mui/icons-material";
import Footer from "../components/Footer";

const convertToInputDate = (dateStr) => {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

// Validation Schema
const validationSchema = Yup.object({
  name: Yup.string()
    .min(3, "Name must be at least 3 characters")
    .max(200, "Name must not exceed 200 characters")
    .required("Name is required"),
  category: Yup.string().required("Category is required"),
  subCategory: Yup.string().required("Sub Category is required"),
  source: Yup.string()
    .min(2, "Source must be at least 2 characters")
    .required("Source is required"),
  shortDescription: Yup.string()
    .min(10, "Description must be at least 10 characters")
    .max(1000, "Description must not exceed 1000 characters")
    .required("Short Description is required"),
  hyperlink: Yup.string()
    .url("Must be a valid URL")
    .required("Hyperlink is required"),
  recurring: Yup.boolean(),
  approved: Yup.boolean(),
  feature: Yup.boolean(),
  internalComments: Yup.string().max(
    500,
    "Comments must not exceed 500 characters"
  ),
});

const Research = () => {
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const statusOption = [
    { id: "1", name: "Active" },
    { id: "0", name: "Deactive" },
  ];

  const { id } = useParams();

  const navigate = useNavigate();

  const [categories, setCategories] = useState({
    categories: [],
    subCategories: [],
  });

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const fetchCategories = useCallback(async () => {
    try {
      const result = await categoryApi.getAllCategories();
      if (result.data?.data?.success) {
        setCategories(result.data?.data?.data);
      }
    } catch (error) {
      console.error(error);
    }
  }, []);

  const formik = useFormik({
    initialValues: {
      name: "",
      category: "",
      subCategory: "",
      source: "",
      shortDescription: "",
      hyperlink: "",
      lastdatetoApply: "",
      recurring: false,
      approved: false,
      feature: false,
      internalComments: "",
      status: "1",
    },
    validationSchema: validationSchema,
    onSubmit: async (values, { resetForm }) => {
      setLoading(true);
      if (id) values.id = id;

      values.recurring ? (values.recurring = "Yes") : (values.recurring = "No");
      values.approved ? (values.approved = "Yes") : (values.approved = "No");
      values.feature ? (values.feature = "Yes") : (values.feature = "No");

      // Keep date in YYYY-MM-DD format for API

      try {
        const response = id
          ? await researchApi.updateOpportunity(values)
          : await researchApi.createOpportunity(values);

        if (response.success) {
          showSnackbar(
            id
              ? "Opportunity updated successfully!"
              : "Opportunity added successfully!"
          );
          resetForm();
          setTimeout(() => {
            navigate("/resources-and-opportunities");
          }, 3000);
        } else {
          showSnackbar(response.error, "error");
        }
      } catch (error) {
        showSnackbar("An error occurred. Please try again.", "error");
      } finally {
        setLoading(false);
      }
    },
  });

  const { setValues } = formik;

  // Removed useCallback fetchOpportunityById to avoid depending on the whole `formik` object
  // Fetching is done inline in useEffect below so formik.setValues won't trigger a refetch loop

  useEffect(() => {
    fetchCategories();

    if (id) {
      (async () => {
        try {
          setLoading(true);
          const result = await researchApi.getOpportunityById(id);
          if (result && result.success) {
            const data = result.data.data;

            setValues({
              name: data.name || "",
              category: data.category ? String(data.category) : "",
              subCategory: data.subCategory ? String(data.subCategory) : "",
              source: data.source || "",
              shortDescription: data.shortDescription || "",
              hyperlink: data.hyperlink || "",
              lastdatetoApply: convertToInputDate(data.lastDateToApply) || "",
              recurring: data.recurring === "Yes",
              approved: data.approved === "Yes",
              feature: data.feature === "Yes",
              internalComments: data.internalComments || "",
              status: String(data.status),
            });
          }
        } catch (error) {
          console.error("Error fetching opportunity:", error);
        } finally {
          setLoading(false);
        }
      })();
    }
  }, [id, fetchCategories, setValues]);

  const handleExcelUpload = async (data) => {
    try {
      const response = await uploadApi.uploadOpportunities(data);

      return response;
    } catch (error) {
      showSnackbar("An error occurred during upload", "error");
      return { success: false, error: error.message };
    }
  };

  return (
    <>
      <Header title="Resources and Opportunities" />
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        {/* Tabs */}
        {!id && (
          <Paper
            elevation={0}
            sx={{
              mb: 3,
              borderRadius: 3,
              overflow: "hidden",
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              sx={{
                "& .MuiTab-root": {
                  textTransform: "none",
                  fontSize: "1rem",
                  fontWeight: 500,
                  minHeight: 64,
                },
                "& .Mui-selected": {
                  color: "primary.main",
                },
                "& .MuiTabs-indicator": {
                  backgroundColor: "primary.main",
                  height: 3,
                },
              }}
            >
              <Tab icon={<Add />} iconPosition="start" label="Manual Entry" />
              <Tab icon={<Upload />} iconPosition="start" label="Bulk Upload" />
            </Tabs>
          </Paper>
        )}
        {/* Manual Entry Form */}
        {tabValue === 0 && (
          <Card
            elevation={0}
            sx={{
              borderRadius: 3,
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            <CardContent sx={{ p: { xs: 3, md: 4 } }}>
              {/* Form Header */}
              <Box sx={{ mb: 4 }}>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 700,
                    color: "primary.main",
                    mb: 1,
                  }}
                >
                  {id ? "Edit Research Opportunity" : "Add New Opportunity"}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {id
                    ? "Modify the details below to update the existing research opportunity."
                    : "Fill in the details below to create a new research opportunity."}
                </Typography>
              </Box>

              <Divider sx={{ mb: 4 }} />

              {/* Form */}
              <Box component="form" onSubmit={formik.handleSubmit}>
                <Grid container spacing={3}>
                  {/* Name */}
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      id="name"
                      name="name"
                      label="Opportunity Name"
                      placeholder="Enter opportunity name"
                      value={formik.values.name}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.name && Boolean(formik.errors.name)}
                      helperText={formik.touched.name && formik.errors.name}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Description sx={{ color: "primary.main" }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          "&:hover fieldset": {
                            borderColor: "primary.main",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "primary.main",
                          },
                        },
                      }}
                    />
                  </Grid>

                  {/* Category */}
                  <Grid item xs={12} md={6}>
                    <FormControl
                      fullWidth
                      error={
                        formik.touched.category &&
                        Boolean(formik.errors.category)
                      }
                    >
                      <InputLabel id="category-label">Category</InputLabel>
                      <Select
                        labelId="category-label"
                        id="category"
                        name="category"
                        value={formik.values.category || ""}
                        label="Category"
                        onChange={(e) => {
                          formik.handleChange(e);
                          formik.setFieldValue("subCategory", "");
                        }}
                        onBlur={formik.handleBlur}
                        startAdornment={
                          <InputAdornment position="start">
                            <CategoryIcon
                              sx={{ color: "primary.main", ml: -0.5 }}
                            />
                          </InputAdornment>
                        }
                        sx={{ bgcolor: "white" }}
                      >
                        <MenuItem value="">
                          <em>Select Category</em>
                        </MenuItem>
                        {categories?.categories?.map((cat) => (
                          <MenuItem key={cat.id} value={String(cat.id)}>
                            {cat.name}
                          </MenuItem>
                        ))}
                      </Select>
                      {formik.touched.category && formik.errors.category && (
                        <FormHelperText>
                          {formik.errors.category}
                        </FormHelperText>
                      )}
                    </FormControl>
                  </Grid>

                  {/* Sub Category */}
                  <Grid item xs={12} md={6}>
                    <FormControl
                      fullWidth
                      error={
                        formik.touched.subCategory &&
                        Boolean(formik.errors.subCategory)
                      }
                    >
                      <InputLabel id="subcategory-label">
                        Subcategory
                      </InputLabel>
                      <Select
                        labelId="subcategory-label"
                        id="subCategory"
                        name="subCategory"
                        value={formik.values.subCategory || ""}
                        label="Subcategory"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        startAdornment={
                          <InputAdornment position="start">
                            <SubdirectoryArrowRight
                              sx={{ color: "primary.main", ml: -0.5 }}
                            />
                          </InputAdornment>
                        }
                        disabled={!formik.values.category} // disable until category selected
                        sx={{ bgcolor: "white" }}
                      >
                        <MenuItem value="">
                          <em>Select Sub Category</em>
                        </MenuItem>
                        {categories?.subCategories
                          ?.filter(
                            (subCat) =>
                              String(subCat.parent_id) ===
                              String(formik.values.category)
                          )
                          ?.map((subCat) => (
                            <MenuItem key={subCat.id} value={String(subCat.id)}>
                              {subCat.name}
                            </MenuItem>
                          ))}
                      </Select>
                      {formik.touched.subCategory &&
                        formik.errors.subCategory && (
                          <FormHelperText>
                            {formik.errors.subCategory}
                          </FormHelperText>
                        )}
                    </FormControl>
                  </Grid>

                  {/* Source */}
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      id="source"
                      name="source"
                      label="Source"
                      placeholder="e.g., Department of Science and Technology"
                      value={formik.values.source}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={
                        formik.touched.source && Boolean(formik.errors.source)
                      }
                      helperText={formik.touched.source && formik.errors.source}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SourceIcon sx={{ color: "primary.main" }} />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>

                  {/* Short Description */}
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      id="shortDescription"
                      name="shortDescription"
                      label="Short Description"
                      placeholder="Provide a brief description of the opportunity"
                      multiline
                      rows={4}
                      value={formik.values.shortDescription}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={
                        formik.touched.shortDescription &&
                        Boolean(formik.errors.shortDescription)
                      }
                      helperText={
                        formik.touched.shortDescription &&
                        formik.errors.shortDescription
                      }
                    />
                  </Grid>

                  {/* Hyperlink */}
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      id="hyperlink"
                      name="hyperlink"
                      label="Hyperlink"
                      placeholder="https://example.com/opportunity"
                      value={formik.values.hyperlink}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={
                        formik.touched.hyperlink &&
                        Boolean(formik.errors.hyperlink)
                      }
                      helperText={
                        formik.touched.hyperlink && formik.errors.hyperlink
                      }
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LinkIcon sx={{ color: "primary.main" }} />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>

                  {/* Last Date to Apply */}
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      id="lastdatetoApply"
                      name="lastdatetoApply"
                      label="Last Date to Apply (Optional)"
                      type="date"
                      value={formik.values.lastdatetoApply || ""}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      InputLabelProps={{ shrink: true }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <CalendarMonth sx={{ color: "primary.main" }} />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>

                  {/* Switches Section */}
                  <Grid item xs={12}>
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 2.5,
                        bgcolor: "#f8f9fa",
                        borderRadius: 2,
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{
                          mb: 2,
                          fontWeight: 600,
                          color: "primary.main",
                        }}
                      >
                        Additional Options
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                id="recurring"
                                name="recurring"
                                checked={formik.values.recurring}
                                onChange={formik.handleChange}
                                sx={{
                                  "&.Mui-checked": {
                                    color: "primary.main",
                                  },
                                }}
                              />
                            }
                            label={
                              <Box>
                                <Typography
                                  variant="body2"
                                  sx={{ fontWeight: 500 }}
                                >
                                  Recurring Opportunity
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  This opportunity repeats periodically
                                </Typography>
                              </Box>
                            }
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                id="approved"
                                name="approved"
                                checked={formik.values.approved}
                                onChange={formik.handleChange}
                                sx={{
                                  "&.Mui-checked": {
                                    color: "primary.main",
                                  },
                                }}
                              />
                            }
                            label={
                              <Box>
                                <Typography
                                  variant="body2"
                                  sx={{ fontWeight: 500 }}
                                >
                                  Approved
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  Mark as approved
                                </Typography>
                              </Box>
                            }
                          />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                id="feature"
                                name="feature"
                                checked={formik.values.feature}
                                onChange={formik.handleChange}
                                sx={{
                                  "&.Mui-checked": {
                                    color: "primary.main",
                                  },
                                }}
                              />
                            }
                            label={
                              <Box>
                                <Typography
                                  variant="body2"
                                  sx={{ fontWeight: 500 }}
                                >
                                  Feature
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  Mark as featured opportunity
                                </Typography>
                              </Box>
                            }
                          />
                        </Grid>
                      </Grid>
                    </Paper>
                  </Grid>

                  {/* Internal Comments */}
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      id="internalComments"
                      name="internalComments"
                      label="Internal Comments (Optional)"
                      placeholder="Add any internal notes or comments"
                      multiline
                      rows={3}
                      value={formik.values.internalComments}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={
                        formik.touched.internalComments &&
                        Boolean(formik.errors.internalComments)
                      }
                      helperText={
                        formik.touched.internalComments &&
                        formik.errors.internalComments
                      }
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Comment sx={{ color: "primary.main" }} />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>

                  {/* Status */}
                  {id && (
                    <Grid item xs={12} md={6}>
                      <FormControl
                        fullWidth
                        error={
                          formik.touched.status && Boolean(formik.errors.status)
                        }
                      >
                        <InputLabel id="status-label">Status</InputLabel>
                        <Select
                          labelId="status-label"
                          id="status"
                          name="status"
                          value={formik.values.status}
                          label="Status"
                          onChange={(e) => {
                            formik.handleChange(e); // ✅ correctly update formik value
                          }}
                          onBlur={formik.handleBlur}
                          startAdornment={
                            <InputAdornment position="start">
                              <CategoryIcon
                                sx={{ color: "primary.main", ml: -0.5 }}
                              />
                            </InputAdornment>
                          }
                          sx={{ bgcolor: "white" }}
                        >
                          <MenuItem value="">
                            <em>Select Status</em>
                          </MenuItem>
                          {statusOption?.map((sts) => (
                            <MenuItem key={sts.id} value={sts.id}>
                              {sts.name}
                            </MenuItem>
                          ))}
                        </Select>
                        {formik.touched.status && formik.errors.status && (
                          <FormHelperText>
                            {formik.errors.status}
                          </FormHelperText>
                        )}
                      </FormControl>
                    </Grid>
                  )}
                  {/* Action Buttons */}
                  <Grid item xs={12}>
                    <Divider sx={{ my: 2 }} />
                    <Box
                      sx={{
                        display: "flex",
                        gap: 2,
                        flexDirection: { xs: "column", sm: "row" },
                        justifyContent: "flex-end",
                      }}
                    >
                      <Button
                        variant="outlined"
                        onClick={() => formik.resetForm()}
                        disabled={loading}
                        sx={{
                          minWidth: { xs: "100%", sm: "120px" },
                          py: 1.5,
                          borderColor: "primary.main",
                          color: "primary.main",
                          "&:hover": {
                            borderColor: "primary.main",
                            bgcolor: "rgba(30, 58, 95, 0.04)",
                          },
                        }}
                      >
                        Reset
                      </Button>
                      <Button
                        type="submit"
                        variant="contained"
                        disabled={loading || !formik.isValid}
                        sx={{
                          minWidth: { xs: "100%", sm: "140px" },
                          py: 1.5,
                          bgcolor: "primary.main",
                          "&:hover": {
                            bgcolor: "#152d4a",
                          },
                        }}
                      >
                        {loading ? (
                          <CircularProgress size={24} color="inherit" />
                        ) : id ? (
                          "Update"
                        ) : (
                          "Submit"
                        )}
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </CardContent>
          </Card>
        )}

        {/* Bulk Upload */}
        {tabValue === 1 && (
          <Card
            elevation={0}
            sx={{
              borderRadius: 3,
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            <CardContent sx={{ p: { xs: 3, md: 4 } }}>
              <ExcelUploader
                onUploadComplete={handleExcelUpload}
                showSnackbar={showSnackbar}
              />
            </CardContent>
          </Card>
        )}

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbar.severity}
            sx={{
              width: "100%",
              boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
      <Footer />
    </>
  );
};

export default Research;
